import { ChiroUpAPI } from '@chiroup/client-core/functions/ChiroUpAPI';
import { ClinicEventType } from '@chiroup/core/enums/ClinicEventType.enum';
import { isEligibleForTTFV } from '@chiroup/core/functions/isEligibleForTTFV';
import { EhrExploreStepId } from '@chiroup/core/types/EhrTrialDashboard.type';
import { Me } from '@chiroup/core/types/Me.type';

/**
 * "This roadmap step is done" signal for the EHR trial dashboard.
 *
 * Lives outside React because the two places a step is earned are far apart:
 * the dashboard itself, and the SAOP sandbox on its own route. Both call this,
 * so the dedupe rule and the storage key can't drift between them.
 *
 * Mirrors `ehrInterest.ts`: count/flag in localStorage, fire the event once,
 * and let the server-side event log be the durable cross-device record. The
 * local copy exists so a step earned in the sandbox still shows complete the
 * moment the user lands back on the dashboard, before the event has made it
 * through SNS into `ClinicEvent`.
 */

const storageKey = (clinicId: number, userId: string) =>
  `chiroup:ehrExplored:${clinicId}:${userId}`;

export const readStoredSteps = (clinicId?: number, userId?: string): string[] => {
  if (!clinicId || !userId) return [];
  try {
    const parsed = JSON.parse(localStorage.getItem(storageKey(clinicId, userId)) || '');

    return Array.isArray(parsed) ? parsed.filter((v): v is string => typeof v === 'string') : [];
  } catch {
    return [];
  }
};

/**
 * Record a step as explored. No-ops when the step is already recorded on this
 * browser, so a user who replays the sandbox doesn't bury the first-completion
 * timestamp the conversion funnel reads.
 *
 * Returns the full local list so a caller holding React state can re-render
 * from it.
 */
export const noteEhrStepExplored = (stepId: EhrExploreStepId, me?: Me | null): string[] => {
  const clinic = me?.selectedClinic;
  const userId = me?.ID;
  // TTFV-eligible clinics only — same enforcement point as noteEhrInterest.
  if (!clinic?.ID || !userId || !isEligibleForTTFV(clinic)) return [];

  const existing = readStoredSteps(clinic.ID, userId);
  if (existing.includes(stepId)) return existing;

  const next = [...existing, stepId];
  try {
    localStorage.setItem(storageKey(clinic.ID, userId), JSON.stringify(next));
  } catch {
    // Private mode / quota. The event below is the durable record; losing the
    // local copy costs a refetch, not the progress.
  }

  ChiroUpAPI.post('api', '/track-event', {
    body: {
      clinicId: clinic.ID,
      locationId: me?.selectedLocation ?? null,
      eventType: ClinicEventType.TrialEhrStepExplored,
      metadata: { stepId },
    },
  }).catch(() => {});

  return next;
};
